// src/components/module/compras/components/ArticulosGrid.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useState } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
  Grid,
  Stack,
  Divider,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { ArticuloCompra, GenericItem } from "../../../types/purchaseOrder.types";
import { ArticuloSearchResult } from "../../../../services/api/articulosApi";
import { DarkModeContext } from "../../../../context/darkModeContext";

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    error: "#E03E3E", errorBg: "#FBE4E4", hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    error: "#D44C47", errorBg: "#3D2020", hover: "rgba(255,255,255,0.04)",
  },
};

interface ArticulosGridProps {
  articulos: ArticuloCompra[];
  newArticulo: Partial<ArticuloCompra>;
  tiposEmbalaje: GenericItem[];
  searchResults: ArticuloSearchResult[];
  isSearching: boolean;
  totals: { subtotal: number; impuestos: number; total: number };
  onArticuloChange: (field: keyof ArticuloCompra, value: any) => void;
  onArticuloSelect: (articulo: ArticuloSearchResult | null) => void;
  onSearch: (searchTerm: string) => void;
  onAdd: () => void;
  onDelete: (index: number) => void;
  validateCantidad: (value: number) => boolean;
  validatePrecio: (value: number) => boolean;
  orderNumber: string;
}

const ArticulosGrid: React.FC<ArticulosGridProps> = ({
  articulos, newArticulo, searchResults, isSearching, totals,
  onArticuloChange, onArticuloSelect, onSearch, onAdd, onDelete,
  validateCantidad, validatePrecio, orderNumber,
}) => {
  const [selectedArticulo, setSelectedArticulo] = useState<ArticuloSearchResult | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const money = (v: number) => `$${v.toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;

  const handleArticuloAdd = () => {
    onAdd();
    setSelectedArticulo(null);
    setSearchInputValue("");
  };

  const thStyle = {
    fontSize: "0.72rem", fontWeight: 600, color: p.textT,
    textTransform: "uppercase" as const, letterSpacing: "0.04em",
    borderBottom: `1px solid ${p.border}`, py: 1, bgcolor: p.bgS,
  };

  const tdStyle = {
    borderBottom: `1px solid ${p.border}`, py: 1.2, fontSize: "0.84rem", color: p.text,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Tabla */}
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{
            borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${p.border}` }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text }}>
                Artículos de la Orden ({articulos.length})
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thStyle, width: "10%" }}>Cant.</TableCell>
                    <TableCell sx={{ ...thStyle, width: "45%" }}>Artículo</TableCell>
                    <TableCell sx={{ ...thStyle, width: "17%" }}>Precio Und.</TableCell>
                    <TableCell sx={{ ...thStyle, width: "17%" }}>Total</TableCell>
                    <TableCell sx={{ ...thStyle, width: "11%", textAlign: "center" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Fila para agregar */}
                  <TableRow sx={{ bgcolor: p.bgS }}>
                    <TableCell sx={{ ...tdStyle, borderBottom: `2px solid ${p.accent}` }}>
                      <TextField
                        type="number" size="small"
                        value={newArticulo.CantidadComprada || 1}
                        onChange={(e) => onArticuloChange("CantidadComprada", Math.max(1, parseInt(e.target.value) || 1))}
                        inputProps={{ min: 1, step: 1, style: { textAlign: "center" } }}
                        fullWidth
                        error={!validateCantidad(newArticulo.CantidadComprada || 1)}
                        sx={{
                          "& .MuiInputBase-root": {
                            borderRadius: "6px", bgcolor: p.bg, fontSize: "0.84rem",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdStyle, borderBottom: `2px solid ${p.accent}` }}>
                      <Autocomplete
                        size="small"
                        options={searchResults}
                        getOptionLabel={(o) => o.articuloNombre}
                        value={selectedArticulo}
                        onChange={(_, v) => { setSelectedArticulo(v); onArticuloSelect(v); }}
                        inputValue={searchInputValue}
                        onInputChange={(_, v) => { setSearchInputValue(v); onSearch(v); }}
                        loading={isSearching}
                        isOptionEqualToValue={(o, v) => o.Id === v?.Id}
                        noOptionsText="Sin resultados"
                        loadingText="Buscando..."
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ py: 1, px: 1.5 }}>
                            <Stack>
                              <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text }}>
                                {option.Nombre}
                              </Typography>
                              <Typography sx={{ fontSize: "0.72rem", color: p.textT }}>
                                EAN: {option.EAN}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar por EAN o nombre..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSearching && <CircularProgress size={16} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-root": {
                                borderRadius: "6px", bgcolor: p.bg, fontSize: "0.84rem",
                              },
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border },
                            }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdStyle, borderBottom: `2px solid ${p.accent}` }}>
                      <TextField
                        type="number" size="small"
                        value={newArticulo.PrecioCompra || ""}
                        onChange={(e) => onArticuloChange("PrecioCompra", parseFloat(e.target.value) || 0)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.8rem", color: p.textT } }}>$</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: "0.01" }}
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            borderRadius: "6px", bgcolor: p.bg, fontSize: "0.84rem",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdStyle, borderBottom: `2px solid ${p.accent}` }}>
                      <Typography sx={{
                        fontSize: "0.88rem", fontWeight: 600, color: p.text,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>
                        {money(Number(newArticulo.Total || 0))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdStyle, borderBottom: `2px solid ${p.accent}`, textAlign: "center" }}>
                      <Tooltip title="Agregar artículo">
                        <span>
                          <IconButton
                            onClick={handleArticuloAdd}
                            disabled={!validateCantidad(newArticulo.CantidadComprada ?? 1) || !validatePrecio(newArticulo.PrecioCompra || 0) || !newArticulo.IdArticulo}
                            sx={{
                              width: 32, height: 32, borderRadius: "8px",
                              bgcolor: p.accentBg, color: p.accent,
                              "&:hover": { bgcolor: darkMode ? "#1C4466" : "#D4E4F7" },
                              "&.Mui-disabled": { opacity: 0.3 },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Artículos agregados */}
                  {articulos.map((art, i) => (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: p.hover }, transition: "background 0.1s" }}>
                      <TableCell sx={{ ...tdStyle, textAlign: "center", fontWeight: 600 }}>
                        {art.CantidadComprada}
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text }}>
                          {art.articulo?.Nombre}
                        </Typography>
                        {art.articulo?.EAN && (
                          <Typography sx={{ fontSize: "0.72rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {art.articulo.EAN}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {money(art.PrecioCompra)}
                      </TableCell>
                      <TableCell sx={{ ...tdStyle, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {money(art.Total)}
                      </TableCell>
                      <TableCell sx={{ ...tdStyle, textAlign: "center" }}>
                        <Tooltip title="Eliminar">
                          <IconButton onClick={() => onDelete(i)} sx={{
                            width: 28, height: 28, borderRadius: "6px",
                            color: p.error, "&:hover": { bgcolor: p.errorBg },
                          }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {articulos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", borderBottom: "none" }}>
                        <Typography sx={{ fontSize: "0.84rem", color: p.textT }}>
                          Busca y agrega artículos a la orden
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Panel resumen */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{
            p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg,
            position: "sticky", top: 20,
          }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text, mb: 2 }}>
              Resumen OC: {orderNumber}
            </Typography>

            <Stack spacing={1.2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>Subtotal</Typography>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {money(totals.subtotal)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>Impuestos</Typography>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {money(totals.impuestos)}
                </Typography>
              </Stack>

              <Divider sx={{ borderColor: p.border }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: p.text }}>Total</Typography>
                <Typography sx={{
                  fontSize: "1.2rem", fontWeight: 700, color: p.accent,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  {money(totals.total)}
                </Typography>
              </Stack>
            </Stack>

            {articulos.length > 0 && (
              <Box sx={{
                mt: 2, pt: 2, borderTop: `1px solid ${p.border}`,
              }}>
                <Typography sx={{ fontSize: "0.72rem", color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {articulos.length} {articulos.length === 1 ? "artículo" : "artículos"}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ArticulosGrid;
